import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { navGroups } from '../adminNavigation';
import { queueCount, useAdminActions } from '../context/AdminActionsContext';
import ActionCount from './ActionCount';

const Sidebar: React.FC = () => {
  const location = useLocation();
  const { summary, error } = useAdminActions();
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => Object.fromEntries(
    navGroups.map(group => [group.id, !group.items.some(item => item.path === location.pathname)]),
  ));
  useEffect(() => {
    const activeGroup = navGroups.find(group => group.items.some(item => item.path === location.pathname));
    if (activeGroup) setCollapsed(value => ({ ...value, [activeGroup.id]: false }));
  }, [location.pathname]);

  return <aside className="fixed left-0 top-0 z-40 h-screen w-[240px] bg-primary flex flex-col shadow-xl">
    <div className="flex items-center gap-3 px-6 py-6 border-b border-white/10">
      <span className="text-2xl" aria-hidden="true">📚</span>
      <div><h1 className="text-white font-bold text-lg tracking-tight leading-tight">Readora</h1>
        <span className="text-white/50 text-xs font-medium uppercase tracking-widest">Admin</span></div>
    </div>
    <nav aria-label="Admin navigation" className="flex-1 py-3 px-3 overflow-y-auto">
      {navGroups.map(group => {
        const groupQueues = summary?.queues.filter(queue => group.items.some(item => item.path === queue.nav_path)) || [];
        const total = queueCount(groupQueues);
        return <section key={group.id} className="mb-2">
          <button type="button" aria-expanded={!collapsed[group.id]} aria-controls={`nav-${group.id}`}
            onClick={() => setCollapsed(value => ({ ...value, [group.id]: !value[group.id] }))}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs font-bold text-white/80 hover:bg-white/10">
            <span className="flex-1">{group.label}</span>
            <ActionCount count={total.count} unknown={total.unavailable || (error && groupQueues.length > 0)} />
            <span aria-hidden="true">{collapsed[group.id] ? '▸' : '▾'}</span>
          </button>
          <div id={`nav-${group.id}`} hidden={!!collapsed[group.id]} className="space-y-0.5">
            {group.items.map(item => {
              const active = location.pathname === item.path;
              const itemQueues = groupQueues.filter(queue => queue.nav_path === item.path);
              const count = queueCount(itemQueues);
              return <NavLink key={item.path} to={item.path} end
                className={`relative flex items-center gap-2 rounded-lg py-2.5 pl-4 pr-3 text-sm font-medium transition-colors ${active ? 'bg-accent text-white' : 'text-white/70 hover:text-white hover:bg-white/10'}`}>
                <span aria-hidden="true" className="w-6 shrink-0 text-center text-base">{item.icon}</span>
                <span className="flex-1">{item.label}</span>
                <ActionCount count={count.count} unknown={count.unavailable || (error && itemQueues.length > 0)} />
                {active && <span className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r bg-white" />}
              </NavLink>;
            })}
          </div>
        </section>;
      })}
    </nav>
    <div className="px-6 py-4 border-t border-white/10 text-xs text-white/40">
      © 2026 Readora
      <p className="mt-1 text-[10px]">{error ? 'Action counts unavailable' : summary ? 'Action counts update every minute' : 'Loading action counts…'}</p>
    </div>
  </aside>;
};

export default Sidebar;
